import type { Map as MapLibreMap, LngLatBoundsLike, LayerSpecification, SourceSpecification } from 'maplibre-gl';
import type {
  MapLayerConfig,
  FlyToOptions,
  MapEventType,
  MapEventHandler,
  BoundingBox,
} from './types';
import { mapExportService } from './MapExportService';
import { mapSnapshotService } from './MapSnapshotService';

/**
 * MapEngineService - Servicio singleton para gestión del mapa
 *
 * @remarks
 * Proporciona una API centralizada para interactuar con el mapa MapLibre.
 * Permite agregar/eliminar capas, controlar la vista, y escuchar eventos.
 *
 * @example
 * ```typescript
 * // Obtener instancia
 * const mapEngine = MapEngineService.getInstance();
 *
 * // Agregar capa de puntos
 * mapEngine.addLayer({
 *   id: 'stops',
 *   type: 'circle',
 *   source: {
 *     type: 'geojson',
 *     data: stopsGeoJSON
 *   },
 *   paint: {
 *     'circle-radius': 6,
 *     'circle-color': '#007cbf'
 *   }
 * });
 *
 * // Escuchar clicks en el mapa
 * mapEngine.on('click', (event) => {
 *   console.log('Click en:', event.lngLat);
 * });
 *
 * // Volar a una ubicación
 * mapEngine.flyTo({
 *   center: [-95, 0],
 *   zoom: 3
 * });
 * ```
 */
export class MapEngineService {
  private static instance: MapEngineService;
  private map: MapLibreMap | null = null;
  private eventListeners: Map<string, Set<MapEventHandler>> = new Map();
  private highlightLayerId: string | null = null;

  /**
   * Constructor privado (patrón Singleton)
   */
  private constructor() {}

  /**
   * Obtiene la instancia única del servicio
   */
  public static getInstance(): MapEngineService {
    if (!MapEngineService.instance) {
      MapEngineService.instance = new MapEngineService();
    }
    return MapEngineService.instance;
  }

  /**
   * Inicializa el servicio con una instancia de mapa
   *
   * @param map - Instancia de MapLibre
   */
  public setMap(map: MapLibreMap): void {
    this.map = map;

    // Exponer mapa globalmente para debugging
    if (typeof window !== 'undefined') {
      (window as any).__MAPLIBRE_MAP__ = map;
    }

    // Registrar mapa en servicios auxiliares
    mapExportService.setMap(map);
    mapSnapshotService.setMap(map);

    // Re-registrar listeners de eventos
    this.eventListeners.forEach((handlers, event) => {
      handlers.forEach(handler => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.map?.on(event as any, handler as any);
      });
    });
  }

  /**
   * Obtiene la instancia de mapa actual
   *
   * @returns Instancia de MapLibre o null si no está inicializado
   */
  public getMap(): MapLibreMap | null {
    return this.map;
  }

  /**
   * Verifica si el mapa está inicializado
   *
   * @throws {Error} Si el mapa no está inicializado
   * @private
   */
  private ensureMapInitialized(): MapLibreMap {
    if (!this.map) {
      throw new Error(
        'MapEngineService: El mapa no ha sido inicializado. Asegúrate de que MapViewer está montado.'
      );
    }
    return this.map;
  }

  /**
   * Agrega una capa al mapa
   *
   * @param layerConfig - Configuración de la capa
   *
   * @remarks
   * Si la capa ya existe, se elimina primero antes de agregarla nuevamente.
   * Retorna false sin lanzar si el style aún no está listo (Fase 2C safe guard).
   */
  public addLayer(layerConfig: MapLayerConfig): boolean {
    const map = this.ensureMapInitialized();

    // Guard: style must exist before any source/layer operations
    if (!isMapReadyForSources(map)) {
      console.warn('[MapEngineService] addLayer: style not ready yet, skipping', layerConfig.id);
      return false;
    }

    // Si la capa ya existe, eliminarla primero
    if (safeGetLayer(map, layerConfig.id)) {
      this.removeLayer(layerConfig.id);
    }

    // Agregar source si es necesario
    if (typeof layerConfig.source !== 'string') {
      const sourceId = `${layerConfig.id}-source`;
      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, layerConfig.source);
      }
      layerConfig.source = sourceId;
    }

    // Agregar capa (construir objeto solo con propiedades definidas)
    const layer: any = {
      id: layerConfig.id,
      type: layerConfig.type,
      source: layerConfig.source,
    };

    // Solo agregar propiedades opcionales si están definidas
    if (layerConfig.paint) layer.paint = layerConfig.paint;
    if (layerConfig.layout) layer.layout = layerConfig.layout;
    if (layerConfig.filter) layer.filter = layerConfig.filter;

    map.addLayer(layer as LayerSpecification, layerConfig.beforeId);
    return true;
  }

  /**
   * Elimina una capa del mapa
   *
   * @param layerId - ID de la capa a eliminar
   */
  public removeLayer(layerId: string): void {
    const map = this.ensureMapInitialized();

    if (safeGetLayer(map, layerId)) {
      try { map.removeLayer(layerId); } catch { /* style not ready — ignore */ }
    }

    // Eliminar source asociado si existe
    const sourceId = `${layerId}-source`;
    if (safeGetSource(map, sourceId)) {
      try { map.removeSource(sourceId); } catch { /* ignore */ }
    }
  }

  /**
   * Verifica si una capa existe en el mapa
   *
   * @param layerId - ID de la capa
   * @returns true si la capa existe
   */
  public hasLayer(layerId: string): boolean {
    const map = this.getMap();
    return !!safeGetLayer(map, layerId);
  }

  /**
   * Obtiene todas las capas del mapa
   *
   * @returns Array de IDs de capas
   */
  public getLayers(): string[] {
    const map = this.getMap();
    if (!map) return [];

    const style = map.getStyle();
    return style.layers ? style.layers.map(layer => layer.id) : [];
  }

  /**
   * Anima la vista del mapa a una ubicación
   *
   * @param options - Opciones de animación
   */
  public flyTo(options: FlyToOptions): void {
    const map = this.ensureMapInitialized();

    // Construir opciones de flyTo solo con propiedades definidas
    // para evitar pasar padding: undefined a MapLibre (causa error interno)
    const flyToOptions: any = {
      center: options.center,
      zoom: options.zoom,
      duration: options.duration ?? 2000,
    };

    // Solo incluir padding si está explícitamente definido
    if (options.padding !== undefined) {
      flyToOptions.padding = options.padding;
    }

    map.flyTo(flyToOptions);
  }

  /**
   * Ajusta el mapa para mostrar un bounding box
   *
   * @param bounds - Bounding box a mostrar
   * @param padding - Padding alrededor del bounds (opcional)
   * @param maxZoom - Zoom máximo (opcional)
   * @param animate - Si debe animar la transición (default: true)
   */
  public fitBounds(
    bounds: BoundingBox,
    padding?: number | { top: number; bottom: number; left: number; right: number },
    maxZoom?: number,
    animate?: boolean
  ): void {
    const map = this.ensureMapInitialized();

    const lngLatBounds: LngLatBoundsLike = [
      [bounds.minLng, bounds.minLat],
      [bounds.maxLng, bounds.maxLat],
    ];

    map.fitBounds(lngLatBounds, {
      padding: padding ?? 50,
      duration: animate === false ? 0 : 1000, // Sin animación en carga inicial
      maxZoom: maxZoom ?? 16,
    });
  }

  /**
   * Restringe la navegación del mapa a un bounding box
   *
   * @param bounds - Bounding box que define los límites de navegación
   *
   * @remarks
   * Los usuarios no podrán hacer pan/zoom fuera de este área.
   * Útil para proyectos con área de estudio definida.
   *
   * @example
   * ```typescript
   * // Restringir navegación al área del proyecto
   * mapEngine.setMaxBounds(project.bounds);
   * ```
   */
  public setMaxBounds(bounds: BoundingBox): void {
    const map = this.ensureMapInitialized();

    const lngLatBounds: LngLatBoundsLike = [
      [bounds.minLng, bounds.minLat],
      [bounds.maxLng, bounds.maxLat],
    ];

    map.setMaxBounds(lngLatBounds);
  }

  /**
   * Elimina las restricciones de navegación del mapa
   *
   * @remarks
   * Después de llamar a este método, los usuarios podrán navegar libremente.
   */
  public clearMaxBounds(): void {
    const map = this.ensureMapInitialized();
    map.setMaxBounds(null);
  }

  /**
   * Registra un listener para eventos del mapa
   *
   * @param event - Tipo de evento
   * @param handler - Función handler
   *
   * @example
   * ```typescript
   * mapEngine.on('click', (event) => {
   *   console.log('Click en:', event.lngLat);
   * });
   * ```
   */
  public on(event: MapEventType, handler: MapEventHandler): void {
    // Agregar al registro de listeners
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(handler);

    // Si el mapa ya está inicializado, registrar el listener
    if (this.map) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.map.on(event as any, handler as any);
    }
  }

  /**
   * Elimina un listener de eventos del mapa
   *
   * @param event - Tipo de evento
   * @param handler - Función handler a eliminar
   */
  public off(event: MapEventType, handler: MapEventHandler): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }

    if (this.map) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.map.off(event as any, handler as any);
    }
  }

  /**
   * Obtiene el nivel de zoom actual
   *
   * @returns Nivel de zoom
   */
  public getZoom(): number {
    const map = this.ensureMapInitialized();
    return map.getZoom();
  }

  /**
   * Establece el nivel de zoom
   *
   * @param zoom - Nivel de zoom
   */
  public setZoom(zoom: number): void {
    const map = this.ensureMapInitialized();
    map.setZoom(zoom);
  }

  /**
   * Obtiene el centro actual del mapa
   *
   * @returns Coordenadas [lng, lat]
   */
  public getCenter(): [number, number] {
    const map = this.ensureMapInitialized();
    const center = map.getCenter();
    return [center.lng, center.lat];
  }

  /**
   * Establece el centro del mapa
   *
   * @param center - Coordenadas [lng, lat]
   */
  public setCenter(center: [number, number]): void {
    const map = this.ensureMapInitialized();
    map.setCenter(center);
  }

  /**
   * Limpia todos los listeners de eventos
   */
  public clearEventListeners(): void {
    this.eventListeners.forEach((handlers, event) => {
      handlers.forEach(handler => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.map?.off(event as any, handler as any);
      });
    });
    this.eventListeners.clear();
  }

  /**
   * Resalta un feature en el mapa
   *
   * @param feature - Feature GeoJSON a resaltar
   * @param layerId - ID de la capa (opcional, usado como sufijo)
   *
   * @remarks
   * Crea una capa temporal de highlight sobre el feature seleccionado.
   * Automáticamente limpia el highlight anterior si existe.
   *
   * @example
   * ```typescript
   * mapEngine.highlightFeature(selectedFeature, 'trees');
   * ```
   */
  public highlightFeature(feature: any, layerId?: string): void {
    const map = this.ensureMapInitialized();

    // Limpiar highlight anterior
    this.clearHighlight();

    // Crear ID único para la capa de highlight
    this.highlightLayerId = layerId ? `highlight-${layerId}` : 'highlight-selected';

    // Determinar el tipo de geometría
    const geometryType = feature.geometry?.type;

    // Crear source para el highlight
    const sourceId = `${this.highlightLayerId}-source`;
    map.addSource(sourceId, {
      type: 'geojson',
      data: feature,
    });

    // Agregar capa de highlight según el tipo de geometría
    if (geometryType === 'Point' || geometryType === 'MultiPoint') {
      // Highlight para puntos - anillo pulsante
      map.addLayer({
        id: this.highlightLayerId,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-radius': 12,
          'circle-color': 'rgba(34, 211, 238, 0.2)',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#22D3EE',
        },
      });
    } else if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
      // Highlight para líneas
      map.addLayer({
        id: this.highlightLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#22D3EE',
          'line-width': 3,
          'line-opacity': 0.8,
        },
      });
    } else if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
      // Highlight para polígonos - borde resaltado
      map.addLayer({
        id: `${this.highlightLayerId}-fill`,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': 'rgba(34, 211, 238, 0.1)',
          'fill-opacity': 0.3,
        },
      });

      map.addLayer({
        id: this.highlightLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#22D3EE',
          'line-width': 2,
          'line-opacity': 1,
        },
      });
    }

    console.log('[MapEngineService] Feature resaltado:', this.highlightLayerId);
  }

  /**
   * Limpia el highlight actual
   *
   * @remarks
   * Elimina la capa de highlight temporal del mapa
   */
  public clearHighlight(): void {
    if (!this.map || !this.highlightLayerId) return;

    // Eliminar capas de highlight
    if (this.map.getLayer(this.highlightLayerId)) {
      this.map.removeLayer(this.highlightLayerId);
    }

    // Para polígonos, también eliminar capa de fill
    const fillLayerId = `${this.highlightLayerId}-fill`;
    if (this.map.getLayer(fillLayerId)) {
      this.map.removeLayer(fillLayerId);
    }

    // Eliminar source
    const sourceId = `${this.highlightLayerId}-source`;
    if (this.map.getSource(sourceId)) {
      this.map.removeSource(sourceId);
    }

    console.log('[MapEngineService] Highlight limpiado:', this.highlightLayerId);
    this.highlightLayerId = null;
  }

  /**
   * Destruye la instancia del servicio
   *
   * @remarks
   * Limpia todos los listeners y referencias al mapa
   */
  public destroy(): void {
    this.clearHighlight();
    this.clearEventListeners();
    this.map = null;
  }
}

/**
 * Exportar instancia singleton por defecto
 */
export const mapEngine = MapEngineService.getInstance();

// ─── Fase 2C: Safe map helpers ────────────────────────────────────────────────
// MapLibre throws several errors before its internal style is initialized.
// These helpers guard all map operations so hooks don't crash when the map
// is mounted early (from cached bounds) but the style isn't loaded yet.
//
// Error cases covered:
//   "Style is not done loading"            — addSource called too early
//   "There is no style added to the map"  — isStyleLoaded / getStyle called too early
//   "Cannot read properties of undefined" — getLayer/getSource internal style=undefined
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true only when MapLibre has a style object ready.
 * Safe to call at any time — never throws.
 * Use as a guard before any getSource / getLayer / addSource / addLayer calls.
 */
export function isMapReadyForSources(
  map: MapLibreMap | null | undefined
): boolean {
  if (!map) return false;
  try {
    // getStyle() throws "There is no style added to the map" before setStyle().
    // If it returns a non-null value, the style object exists.
    const style = map.getStyle?.();
    return style != null;
  } catch {
    return false;
  }
}

/**
 * Safe isStyleLoaded — returns false instead of throwing before style exists.
 */
export function safeIsStyleLoaded(
  map: MapLibreMap | null | undefined
): boolean {
  if (!isMapReadyForSources(map)) return false;
  try {
    return !!map!.isStyleLoaded();
  } catch {
    return false;
  }
}

/**
 * Safe getSource — returns undefined instead of throwing when style not ready.
 */
export function safeGetSource<T = ReturnType<MapLibreMap['getSource']>>(
  map: MapLibreMap | null | undefined,
  sourceId: string
): T | undefined {
  if (!isMapReadyForSources(map)) return undefined;
  try {
    return map!.getSource(sourceId) as T | undefined;
  } catch {
    return undefined;
  }
}

/**
 * Safe getLayer — returns undefined instead of throwing when style not ready.
 */
export function safeGetLayer(
  map: MapLibreMap | null | undefined,
  layerId: string
): ReturnType<MapLibreMap['getLayer']> {
  if (!isMapReadyForSources(map)) return undefined;
  try {
    return map!.getLayer(layerId);
  } catch {
    return undefined;
  }
}

/**
 * Safe addSource.
 * Returns true if source was added (or already existed).
 * Returns false without throwing when style not ready or "not done loading".
 * Re-throws unexpected errors.
 */
export function safeAddSource(
  map: MapLibreMap | null | undefined,
  sourceId: string,
  sourceDef: SourceSpecification
): boolean {
  if (!isMapReadyForSources(map)) return false;
  try {
    if (!map!.getSource(sourceId)) {
      map!.addSource(sourceId, sourceDef);
    }
    return true;
  } catch (error) {
    const msg = String(error);
    if (
      msg.includes('Style is not done loading') ||
      msg.includes('no style added') ||
      msg.includes('There is no style')
    ) {
      return false;
    }
    throw error;
  }
}

// Exponer mapEngine a window para debugging y E2E tests
if (typeof window !== 'undefined') {
  (window as any).mapEngine = mapEngine;
}
